
/**
 * Product Page Initialization
 * Handles web component registration and scoped initialization for main product and quick view.
 */

// Define Web Components globally (can only be defined once)
const defineCustomElement = (name, componentClass) => {
  if (!customElements.get(name)) {
    customElements.define(name, componentClass);
  }
};

class DeferredMedia extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-deferred-media-toggler]');
    this.template = this.querySelector('template');
    const loadMedia = () => {
      if (this.dataset.onHover !== 'true' && typeof window.pauseElementBasedMedia === 'function') {
        window.pauseElementBasedMedia(document.body, this);
      }
      if (this.getAttribute('loaded') === 'true') return;
      this.setAttribute('loaded', 'true');
      if (this.template) {
        const content = this.template.content.cloneNode(true);
        this.appendChild(content);
      }
      const mediaElement = this.querySelector('video, iframe');
      if (mediaElement) {
        mediaElement.focus();
        if (mediaElement.tagName === 'VIDEO') {
          mediaElement.play().catch(() => {});
        } else if (mediaElement.tagName === 'IFRAME') {
          const src = mediaElement.getAttribute('src');
          if (src && !src.includes('autoplay')) {
            mediaElement.setAttribute('src', src.includes('?') ? `${src}&autoplay=1` : `${src}?autoplay=1`);
          }
        }
      }
    };
    this.loadMediaHandler = loadMedia;
    if (this.button) this.button.addEventListener('click', this.loadMediaHandler);
  }
  disconnectedCallback() {
    if (this.button && this.loadMediaHandler) {
      this.button.removeEventListener('click', this.loadMediaHandler);
    }
  }
}
defineCustomElement('deferred-media', DeferredMedia);

class ProductModel extends DeferredMedia {
  connectedCallback() {
    super.connectedCallback();
    const originalHandler = this.loadMediaHandler;
    this.loadMediaHandler = () => {
      originalHandler();
      const smv = (errors) => {
        if (errors) return;
        this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'));
      };
      Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: smv,
        }
      ]);
    };
    if (this.button) {
      this.button.removeEventListener('click', originalHandler);
      this.button.addEventListener('click', this.loadMediaHandler);
    }
  }
}
defineCustomElement('product-model', ProductModel);

class VariantPill extends HTMLElement {
  constructor() {
    super();
    this.variantChange = this.onInputChange.bind(this);
    this.addEventListener('change', this.variantChange);
  }

  onInputChange() {
    const input = this.querySelector('input[type="radio"]:checked');
    this.setVariantOptions(input.value);
  }

  setVariantOptions(value) {
    const root = this.closest('.shopify-section, .modal-component') || document;
    const parent = root.querySelector(this.dataset.parent);
    const errElem = this.dataset.errorSelector && this.dataset.errorSelector !== '[]' ? root.querySelector(this.dataset.errorSelector) : null;
    const selElem = this.dataset.selectedParent && this.dataset.selectedParent !== '[]' ? root.querySelector(this.dataset.selectedParent) : null;
    if (!parent) return;
    if (errElem) errElem.classList.add('hidden');
    if (selElem) selElem.innerText = value;
    const currentSet = JSON.parse(this.dataset.currentSet);
    const index = parseInt(this.dataset.optionIndex);
    currentSet[index] = value;
    const pills = parent.querySelectorAll('variant-pill');
    pills.forEach((pill) => pill.setAttribute('data-current-set', JSON.stringify(currentSet)));
    this.setOptions(pills, currentSet);
    let availableSet = null;
    if (this.dataset.checkSets === 'true') {
      const sets = JSON.parse(this.dataset.availableSets);
      const checkSet = (val) => {
        if (!val || !Array.isArray(sets)) return null;
        return sets.find((set) => set.includes(val));
      };
      availableSet = checkSet(value);
    }
    onVariantChange(
      this.dataset.jsonSelector,
      availableSet ? availableSet : currentSet,
      this.dataset.forms,
      this.dataset.renders,
      this.dataset.mainParent,
      this.dataset.galleries,
      this.dataset.sectionId,
      this.dataset.productUrl,
      this.dataset.updateUrl,
      this.dataset.focusSelector ? this.dataset.focusSelector : null,
      this.dataset.mediaTemplate === 'true' ? true : false,
      this.dataset.mediaTemplate === 'true' ? this.dataset.template : null,
      root
    );
  }

  setOptions(elems, set) {
    const root = this.closest('.shopify-section, .modal-component') || document;
    const json = root.querySelector(this.dataset.jsonSelector);
    const data = JSON.parse(json.textContent);
    const variants = data.variants;
    const selectedOptionOneVariants = variants.filter((variant) => set[0] === variant.option1);
    elems.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"]')];
      const previousOptionSelected = elems[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((elem) => {
      const label = elem.nextElementSibling;
      const labelText = label.querySelector('[data-variant-status-text]');
      if (listOfAvailableOptions.includes(elem.getAttribute('value'))) {
        elem.classList.remove('disabled');
        labelText.classList.add('hidden');
        return;
      }
      elem.classList.add('disabled');
      labelText.classList.remove('hidden');
    });
  }
}
defineCustomElement('variant-pill', VariantPill);
window.AtcSubmit = class extends HTMLElement {
  connectedCallback() {
    const root = this.closest('.shopify-section, .modal-component') || document;
    
    // Try multiple ways to find the form
    this.form = this.closest('form') || 
                (this.dataset.form ? root.querySelector(this.dataset.form) : null) ||
                root.querySelector('form[action*="/cart/add"]');
    
    if (!this.form) return;
    


    const onAddToCart = async (event) => {
      event.preventDefault();
      
      const items = [];
      const collectLineItemProperties = () => {
        const props = {};
        this.form.querySelectorAll('input[name^="properties["], textarea[name^="properties["], select[name^="properties["]').forEach((field) => {
          if (field.disabled) return;
          const name = field.getAttribute('name');
          if (!name) return;
          const match = name.match(/^properties\[(.*)\]$/);
          if (!match || !match[1]) return;
          if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
          if (field.value === undefined || field.value === null || String(field.value).trim() === '') return;
          props[match[1]] = String(field.value).trim();
        });
        return props;
      };
      // Main product
      const mainIdInput = this.form.querySelector('input[name="id"]');
      const mainQtyInput = this.form.querySelector('input[name="quantity"]');
      const mainSellingPlanInput = this.form.querySelector('input[name="selling_plan"]:not(:disabled)');
      const activeBundleBtn = root.querySelector('bundle-package button[data-active="true"]');
      
      if (mainIdInput && mainIdInput.value) {
        const mainItem = {
          id: mainIdInput.value,
          quantity: parseInt(mainQtyInput ? mainQtyInput.value : 1)
        };

        const properties = collectLineItemProperties();
        if (activeBundleBtn) {
          const selectedBundleQty = parseInt(activeBundleBtn.dataset.bundleQty || '1', 10);
          if (Number.isFinite(selectedBundleQty) && selectedBundleQty > 0) {
            properties._bundle_qty = String(selectedBundleQty);
          }
        }
        if (Object.keys(properties).length > 0) {
          mainItem.properties = properties;
        }

        if (mainSellingPlanInput && mainSellingPlanInput.value) {
          mainItem.selling_plan = parseInt(mainSellingPlanInput.value);
        }

        items.push(mainItem);
      }

      const addonCheckboxes = root.querySelectorAll('.js-product-addon-checkbox:checked');
      addonCheckboxes.forEach(checkbox => {
        if (checkbox.dataset.addonVariantId) {
          items.push({ id: checkbox.dataset.addonVariantId, quantity: 1 });
        }
      });

      const giftLogic = root.querySelector('gift-logic');
      if (giftLogic && giftLogic.unlockedGifts) {
        giftLogic.unlockedGifts.forEach(variantId => {
          if (variantId && !items.find(item => String(item.id) === String(variantId))) {
            items.push({ id: variantId, quantity: 1 });
          }
        });
      }

      const secArr = JSON.parse(this.dataset.sections || '[]');

      // Bundle product: resolve correct variant from product.js before submitting
      const bundleEls = root.querySelectorAll('bundle-package');
      if (bundleEls.length > 0) {
        const activeBtn = root.querySelector('bundle-package button[data-active="true"]');
        let bundleVariantId = activeBtn?.dataset?.variantId?.trim() || '';
        let bundlePlanId = activeBtn?.dataset?.sellingPlanId?.trim() || '';
        const bundleQty = parseInt(activeBtn?.dataset?.bundleQty || '1', 10);

        // Get product handle from inline page JSON (reliable, no URL parsing)
        const getPageHandle = () => {
          const el = root.querySelector('script[id^="ProductJson-"]') ||
                     document.querySelector('script[id^="ProductJson-"]');
          if (!el) return null;
          try { return JSON.parse(el.textContent).handle || null; } catch (_) { return null; }
        };

        const handle = getPageHandle()
          || window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1];

        if (handle) {
          try {
            toggleLoader(true);
            const prod = await window.fetch(`/products/${handle}.js`).then(r => r.json());
            const variants = prod.variants || [];
            console.log('[ATC Bundle] product.js variants:', variants.map(v => v.id));

            // Find variant matching the selling plan from button
            let correctVariant = null;
            if (bundlePlanId) {
              correctVariant = variants.find(v =>
                (v.selling_plan_allocations || []).some(a => String(a.selling_plan_id) === bundlePlanId)
              );
            }
            // Fallback: any variant with selling plan allocations
            if (!correctVariant) {
              correctVariant = variants.find(v => (v.selling_plan_allocations || []).length > 0);
            }

            if (correctVariant) {
              bundleVariantId = String(correctVariant.id);
              // Keep bundlePlanId if valid for this variant, otherwise use first allocation
              const hasMatchingPlan = (correctVariant.selling_plan_allocations || [])
                .some(a => String(a.selling_plan_id) === bundlePlanId);
              if (!hasMatchingPlan && (correctVariant.selling_plan_allocations || []).length > 0) {
                bundlePlanId = String(correctVariant.selling_plan_allocations[0].selling_plan_id);
              }
            }
          } catch (e) {
            console.log('[ATC Bundle] product.js fetch failed:', String(e));
          } finally {
            toggleLoader(false);
          }
        }

        if (!bundleVariantId) { return; }

        const bundleItem = { id: parseInt(bundleVariantId, 10), quantity: 1 };
        if (bundlePlanId) bundleItem.selling_plan = parseInt(bundlePlanId, 10);
        if (Number.isFinite(bundleQty) && bundleQty > 0) {
          bundleItem.properties = { _bundle_qty: String(bundleQty) };
        }

        console.log('[ATC Bundle] submitting:', JSON.stringify(bundleItem));

        const config = fetchConfig('json');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: [bundleItem],
          sections: secArr.map(s => s.id),
          sections_url: window.location.pathname
        });
        fetchAtc(secArr, config, this, root);
        return;
      }

      if (items.length === 0) return;

      const config = fetchConfig('json');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items: items,
        sections: secArr.map((section) => section.id),
        sections_url: window.location.pathname
      });

      fetchAtc(secArr, config, this, root);
    };
    
    this.submitHandler = onAddToCart.bind(this);
    this.form.addEventListener('submit', this.submitHandler);
  }
};

if (!customElements.get('atc-submit')) {
  customElements.define('atc-submit', AtcSubmit);
}

class GiftLogic extends HTMLElement {
  constructor() {
    super();
    this.unlockedGifts = [];
  }

  connectedCallback() {
    const root = this.closest('.shopify-section') || document;
    this._root = root;
    this.form = root.querySelector(`form[id^="main-product-atc-form"]`) || root.querySelector('form[action*="/cart/add"]');
    if (!this.form) return;

    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.priceElement = root.querySelector('[data-main-product-price-render]');
    
    this.updateLogic = this.checkThresholds.bind(this);
    
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.updateLogic);
      this.quantityInput.addEventListener('input', this.updateLogic);
    }
    
    if (this.priceElement) {
      this.observer = new MutationObserver(this.updateLogic);
      this.observer.observe(this.priceElement, { childList: true, subtree: true, characterData: true });
    }

    root.addEventListener('variant:change', this.updateLogic);
    setTimeout(this.updateLogic, 100);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this._root) this._root.removeEventListener('variant:change', this.updateLogic);
  }

  checkThresholds() {
    const qty = parseInt(this.quantityInput ? this.quantityInput.value : 1);
    const priceText = this.priceElement ? this.priceElement.textContent : "0";
    const productPrice = parseFloat(priceText.replace(/[^0-9.]/g, '') || 0);
    const totalPrice = qty * productPrice;
    
    const giftItems = this.querySelectorAll('[data-threshold]');
    this.unlockedGifts = [];

    giftItems.forEach(item => {
      const threshold = parseFloat(item.dataset.threshold);
      const variantId = item.dataset.variantId;
      const basis = item.dataset.unlockBasis || 'price';
      const comparisonValue = basis === 'price' ? totalPrice : qty;

      if (variantId && comparisonValue >= threshold) {
        item.classList.add('is-unlocked');
        item.classList.remove('is-locked');
        this.unlockedGifts.push(variantId);
      } else {
        item.classList.remove('is-unlocked');
        item.classList.add('is-locked');
      }
    });
  }
}
defineCustomElement('gift-logic', GiftLogic);

class BundlePackage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onSelect.bind(this));
    const btn = this.querySelector('button');
    if (btn && btn.dataset.active === 'true') {
      this.syncSelection(btn);
    }
  }

  syncSelection(btn) {
    const root = this.closest('.shopify-section') || document;
    if (!btn) return;
    const qtyInput = root.querySelector('input[name="quantity"][data-min]');
    if (qtyInput) {
      // Bundle selection switches variant/plan only; keep cart line-item quantity as one.
      qtyInput.value = 1;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;

    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const variantId = btn.dataset.variantId ? btn.dataset.variantId.trim() : '';
    const sellingPlanId = btn.dataset.sellingPlanId ? btn.dataset.sellingPlanId.trim() : '';

    if (variantId) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
      }
    }

    if (sellingPlanInput) {
      if (sellingPlanId) {
        sellingPlanInput.value = sellingPlanId;
        sellingPlanInput.disabled = false;
      } else {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }
    }

    if (window.location.pathname.startsWith('/products/')) {
      const url = new URL(window.location.href);
      if (variantId) {
        url.searchParams.set('variant', variantId);
      }
      if (sellingPlanId) {
        url.searchParams.set('selling_plan', sellingPlanId);
      } else {
        url.searchParams.delete('selling_plan');
      }
      window.history.replaceState({}, '', url.toString());
    }
  }

  onSelect(event) {
    const clickedBtn = event.target.closest('button');
    if (!clickedBtn) return;
    const colsHolder = this.closest('.bundle-packs_cols-holder');
    if (!colsHolder) return;

    // Find the prefix from button classes (e.g., bps-one, bps-two)
    const prefix = Array.from(clickedBtn.classList).find(cls => cls.startsWith('bps-') && !cls.endsWith('--active'));
    const activeClass = prefix ? `${prefix}--active` : null;

    colsHolder.querySelectorAll('bundle-package button').forEach((el) => {
      // Remove any class ending in --active
      const classesToRemove = Array.from(el.classList).filter(cls => cls.endsWith('--active'));
      classesToRemove.forEach(cls => el.classList.remove(cls));
      el.removeAttribute('data-active');
    });

    if (activeClass) clickedBtn.classList.add(activeClass);
    clickedBtn.dataset.active = 'true';
    this.syncSelection(clickedBtn);
  }
}
defineCustomElement('bundle-package', BundlePackage);

class RecipientForm extends HTMLElement {
  connectedCallback() {
    this.checkboxInput = this.querySelector(`[id*="Recipient-Checkbox"]`);
    this.checkboxInput.disabled = false;
    this.hiddenControlField = this.querySelector(`[id*="Recipient-Control"]`);
    this.hiddenControlField.disabled = true;
    this.emailInput = this.querySelector(`[id*="Recipient-email"]`);
    this.nameInput = this.querySelector(`[id*="Recipient-name"]`);
    this.messageInput = this.querySelector(`[id*="Recipient-message"]`);
    this.sendonInput = this.querySelector(`[id*="Recipient-send-on"]`);
    this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
    this.errorMessageList = this.errorMessageWrapper.querySelector('ul');
    this.errorMessage = this.errorMessageWrapper.querySelector('.error-message');
    this.defaultErrorHeader = this.errorMessage?.innerText;
    this.currentProductVariantId = this.dataset.productVariantId;
    this.addEventListener('change', this.onChange.bind(this));
  }

  onChange() {
    if (!this.checkboxInput.checked) {
      this.clearInputFields();
      this.clearErrorMessage();
    }
  }

  clearInputFields(flag = false) {
    if (this.emailInput) this.emailInput.value = '';
    if (this.nameInput) this.nameInput.value = '';
    if (this.messageInput) this.messageInput.value = '';
    if (this.sendonInput) this.sendonInput.value = '';
    if (flag) this.checkboxInput.checked = false;
  }

  displayErrorMessage(title, body) {
    this.clearErrorMessage();
    this.errorMessageWrapper.hidden = false;
    if (typeof body === 'object') {
      this.errorMessage.innerText = this.defaultErrorHeader;
      return Object.entries(body).forEach(([key, value]) => {
        const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}-${this.dataset.productId}`;
        const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}-${this.dataset.productId}`;
        const label = key.includes('send_on') ? '' : key;
        const message = `${label} ${value}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);
        const errorTextElement = errorMessageElement?.querySelector('.error-message');
        if (!errorTextElement) return;
        if (this.errorMessageList)
          this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
        errorTextElement.innerText = `${message}.`;
        errorMessageElement.classList.remove('hidden');
        const inputElement = this[`${key}Input`];
        if (!inputElement) return;
        inputElement.setAttribute('aria-invalid', true);
        inputElement.setAttribute('aria-describedby', errorMessageId);
      });
    }
    this.errorMessage.innerText = body;
  }

  createErrorListItem(target, message) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.setAttribute('href', target);
    a.innerText = message;
    a.classList.add('errors-list_link');
    li.appendChild(a);
    li.className = 'error-message errors-list_item';
    return li;
  }

  clearErrorMessage() {
    this.errorMessageWrapper.hidden = true;
    if (this.errorMessageList) this.errorMessageList.innerHTML = '';
    this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
      field.classList.add('hidden');
      const textField = field.querySelector('.error-message');
      if (textField) textField.innerText = '';
    });
    [this.emailInput, this.messageInput, this.nameInput].forEach((inputElement) => {
      inputElement.setAttribute('aria-invalid', false);
      inputElement.removeAttribute('aria-describedby');
    });
  }

  resetRecipientForm() {
    if (this.checkboxInput.checked) {
      this.checkboxInput.checked = false;
      this.clearInputFields();
      this.clearErrorMessage();
    }
  }
}
defineCustomElement('recipient-form', RecipientForm);

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    const onButtonClick = (event) => {
      event.preventDefault();
      const previousValue = this.input.value;
      event.target.name === 'plus' || event.target.parentElement.name === 'plus'
        ? this.input.stepUp()
        : this.input.stepDown();
      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    };

    const setBtnStatus = (type, value) => {
      const currVal = type === 'min' ? parseInt(this.input.min) : parseInt(this.input.value);
      const btn = type === 'min' ? this.querySelector("button[name='minus']") : this.querySelector("button[name='plus']");
      if (!btn) return;
      switch (type) {
        case 'min':
          btn.classList.toggle('disabled', value <= currVal);
          btn.toggleAttribute('disabled', value <= currVal);
          break;
        case 'max':
          btn.classList.toggle('disabled', value >= currVal);
          btn.toggleAttribute('disabled', value >= currVal);
          break;
      }
    };

    const setSubtotals = (qty, elem) => {
      const total = qty * parseInt(this.dataset.variantPrice);
      elem.innerHTML = Shopify.formatMoney(total, window.money_format);
    };

    const onInputChange = () => {
      const root = this.closest('.shopify-section') || document;
      const value = parseInt(this.input.value);
      const qtySubtotal = this.querySelector('[data-qty-subtotal]') || root.querySelector('[data-qty-single-subtotal]');
      this.input.min && setBtnStatus('min', value), this.input.max && setBtnStatus('max', value);
      if (!qtySubtotal) return;
      setSubtotals(value, qtySubtotal);
    };

    const setListeners = () => {
      this.changeEvent = new Event('input', { bubbles: true });
      this.inputChange = onInputChange.bind(this);
      this.buttonClick = onButtonClick.bind(this);
      this.input.addEventListener('input', this.inputChange);
      this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.buttonClick));
    };

    const initialize = () => {
      this.input = this.querySelector('input');
      if (!this.input) return;
      setListeners();
    };
    initialize();
  }
}
defineCustomElement('quantity-input', QuantityInput);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.empty = true;
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('.product-recommendations');
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.empty = false;
            this.innerHTML = recommendations.innerHTML;
            if (!recommendations.innerHTML.includes('data-recommend-check')) {
              let recommendationAncestor = this.parentElement;
              while (recommendationAncestor && !recommendationAncestor.classList.contains('recommendation')) {
                recommendationAncestor = recommendationAncestor.parentElement;
              }
              if (recommendationAncestor) recommendationAncestor.classList.add('hidden');
            }
          }
        })
        .catch((e) => console.error(e))
        .finally(() => {
          if (this.empty) this.parentElement.classList.add('hidden');
        });
    };
    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}
defineCustomElement('product-recommendations', ProductRecommendations);

// Global Utilities
window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: this._sxr.bind(this) }]);
  },
  _sxr(errors) {
    if (errors) return;
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this._sxr());
      return;
    }
    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.Shopify.formatMoney = function(cents, format) {
  if (typeof cents == 'string') { cents = cents.replace('.',''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || this.money_format);
  function defaultOption(opt, def) { return (typeof opt == 'undefined' ? def : opt); }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal   = defaultOption(decimal, '.');
    if (isNaN(number) || number == null) { return 0; }
    number = (number/100.0).toFixed(precision);
    var parts   = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents   = parts[1] ? (decimal + parts[1]) : '';
    return dollars + cents;
  }
  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount': value = formatWithDelimiters(cents, 2); break;
    case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
    case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
  }
  return formatString.replace(placeholderRegex, value);
};

const toggleLoader = (state) => {
  const loader = document.querySelector('[data-theme-loader]');
  if (!loader) return;
  if (state) {
    loader.style.display = 'flex';
    loader.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, fill: 'forwards', easing: 'ease' }).onfinish = () => {
      loader.classList.add('theme-loader--active');
    };
    return;
  }
  loader.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'forwards', easing: 'ease' }).onfinish = () => {
    loader.classList.remove('theme-loader--active');
    loader.style.display = 'none';
  };
};

const getJsonParse = (attrStr, root = document) => {
  let arr = [];
  const strArr = attrStr ? JSON.parse(attrStr) : null;
  if (!strArr) return (arr = null);
  strArr.forEach((selector) => {
    const element = root.querySelector(selector);
    if (element) arr.push(element);
  });
  return arr;
};

const updateLinks = (variant, root = document, parentSelector) => {
  const parentElem = root.querySelector(parentSelector);
  if (!parentElem) return;
  const links = parentElem.querySelectorAll('[data-update-link]');
  links.forEach((link) => {
    const url = new URL(link.href);
    url.searchParams.set('variant', variant);
    link.href = url.toString();
  });
};

const fetchConfig = (type = 'json') => ({
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
});

const onVariantChange = (jsonSelector, optSet, formsSelector, renders, parentSelector, gallerySelectors, sectionId, productUrl, updateUrl, focusSelector, renderMedia, templateSelector, root = document) => {
  const json = root.querySelector(jsonSelector);
  if (!json) return;
  const data = JSON.parse(json.textContent);
  const variants = data.variants;
  const currentVariant = variants.find((variant) => variant.options.every((option, index) => option === optSet[index]));
  setStatuses(currentVariant, formsSelector, renders, parentSelector, root);
  if (!currentVariant) return;
  if (currentVariant.featured_image) setGallery(currentVariant.featured_image, gallerySelectors, renderMedia, templateSelector, root);
  renderVariant(renders, currentVariant.id, parentSelector, productUrl, sectionId, updateUrl, focusSelector, root);
  root.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: currentVariant } }));
};

const changeText = (element, text) => {
  const buttonText = element.querySelector('.button_text');
  if (buttonText) {
    buttonText.textContent = text;
  } else {
    for (const node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = text;
        break;
      }
    }
  }
};

const setStatuses = (variant, formElems, renderElems, parentSelector, root = document) => {
  const forms = getJsonParse(formElems, root);
  if (!forms) return;
  forms.forEach((form) => {
    if (!form) return;
    const atcBtn = form.querySelector('[name="add"]');
    if (!variant) return toggleAvail(false, renderElems, root, parentSelector, atcBtn);
    toggleAvail(true, renderElems, root, parentSelector);
    const input = form.querySelector('[name="id"]');
    input.value = variant.id;
    if (!atcBtn) return;
    if (variant.available) {
      changeText(atcBtn, atcBtn.dataset.textAvail);
      atcBtn.removeAttribute('disabled');
      return;
    }
    changeText(atcBtn, atcBtn.dataset.textSold);
    atcBtn.setAttribute('disabled', '');
  });
};

const setGallery = (imageId, galleries, render, template, root = document) => {
  const arr = JSON.parse(galleries);
  if (render) {
    const templateElem = root.querySelector(template);
    const templateElement = templateElem.content.querySelector(`[data-media-id="${imageId}"]`);
    const imageContainer = root.querySelector(arr[0]);
    var currentOpacity = parseFloat(imageContainer.style.opacity) || 1;
    var newOpacity = currentOpacity === 1 ? 0 : 1;
    imageContainer.style.transition = 'opacity 0.3s ease';
    imageContainer.style.opacity = newOpacity;
    setTimeout(() => {
      imageContainer.innerHTML = templateElement.outerHTML;
      setTimeout(() => { imageContainer.style.opacity = 1; }, 0);
    }, 500);
    return;
  }
  arr.forEach((gallery) => {
    const gal = root.querySelector(gallery);
    if (!gal) return;
    switch (gal.slider) {
      case undefined:
        let items = Array.from(gal.querySelectorAll('[data-gal-item]'));
        const activeIndex = items.findIndex((item) => item.dataset.mediaId == imageId);
        if (activeIndex === -1 || activeIndex === items[activeIndex]) return;
        items.unshift(items.splice(activeIndex, 1)[0]);
        gal.innerHTML = '';
        gal.classList.add('--swapped');
        gal.append(...items);
        break;
      default:
        const splide = gal.slider;
        let slide = splide.Components.Slides.filter(`[data-media-id="${imageId}"]`);
        if (slide[0]) return splide.go(slide[0].index);
        gal.querySelector(`[data-media-id="${imageId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        break;
    }
  });
};

const renderVariant = (elems, id, parent, url, section, updateUrl, focusSelector, root = document) => {
  toggleLoader(true);
  fetch(`${url}?variant=${id}&section_id=${section}`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const parentElement = root.querySelector(parent);
      if (!parentElement) return;
      const arr = JSON.parse(elems);
      arr.forEach((selector) => {
        const element = parentElement.querySelector(selector);
        const source = html.querySelector(selector);
        if (element && source) element.innerHTML = source.innerHTML;
      });
    })
    .catch((e) => console.error(e))
    .finally(() => {
      // Only update URL if we are actually on the product page
      // This prevents URL changes in Quick View (Home/Collections)
      const isProductPage = window.location.pathname === url;
      
      if (updateUrl === 'true' && isProductPage) {
        window.history.replaceState({}, '', `${url}?variant=${id}`);
      }
      if (focusSelector) {
        const focusParent = root.querySelector(focusSelector);
        if (focusParent) trapFocus(focusParent.content ? focusParent.content : focusParent);
      }
      toggleLoader(false);
    });
};

const toggleAvail = (flag, selectors, root = document, parentSelector, btn) => {
  const arr = JSON.parse(selectors);
  const parentElem = root.querySelector(parentSelector);
  arr.forEach((selector) => {
    const element = parentElem.querySelector(selector);
    if (!element) return;
    flag ? element.classList.remove('--elem-opac') : element.classList.add('--elem-opac');
  });
  if (!btn) return;
  changeText(btn, btn.dataset.textUnavail);
  btn.setAttribute('disabled', '');
};

const sectionArrRender = (secArr, response) => {
  secArr.forEach((section) => {
    const html = new DOMParser().parseFromString(response.sections[section.id], 'text/html').getElementById(section.id);
    section.selectors.forEach((selector) => {
      const targetElement = document.querySelector(selector);
      const sourceElement = html.querySelector(selector);
      if (targetElement && sourceElement) targetElement.outerHTML = sourceElement.outerHTML;
    });
  });
};

const resetQty = (elem, root = document) => {
  const form = root.querySelector(elem);
  if (!form) return;
  const qtyField = form.quantity;
  if (qtyField) {
    qtyField.value = parseFloat(qtyField.min || 1);
    qtyField.dispatchEvent(new Event('input'));
  }
};

const atcError = (response, elem, flag, root = document) => {
  const errElem = root.querySelector(elem.dataset.errorSelector);
  if (!errElem) return;
  const errSpan = errElem.querySelector('[data-error]');
  const recepForm = elem.form.querySelector('recipient-form');
  if (!flag) {
    errElem.classList.add('hidden');
    if (errSpan) errSpan.innerText = '';
    if (recepForm) recepForm.clearErrorMessage();
    return;
  }
  if (recepForm && response.errors) recepForm.displayErrorMessage(null, response.errors);
  if (errSpan && typeof response.description !== 'object') {
    errElem.classList.remove('hidden');
    errSpan.innerText = response.description;
  }
  resetQty(elem.dataset.form, root);
};

const fetchAtc = (sections, config, element, root = document) => {
  let hasError = true;
  toggleLoader(true);
  atcError(null, element, false, root);
  
  const url = routes.cart_add_url.endsWith('.js') ? routes.cart_add_url : `${routes.cart_add_url}.js`;
  
  fetch(url, config)
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422 || response.errors) {
        return atcError(response, element, true, root);
      }
      
      hasError = false;
      if (response.sections && sections.length > 0) {
        sectionArrRender(sections, response);
      }
    })
    .catch((e) => {

      atcError({ description: 'Something went wrong. Please try again.' }, element, true, root);
    })
    .finally(() => {
      resetQty(element.dataset.form, root);
      toggleLoader(false);
      if (!hasError) {
        const addonCheckboxes = root.querySelectorAll('.js-product-addon-checkbox:checked');
        addonCheckboxes.forEach(checkbox => checkbox.checked = false);
        if (element.dataset.selfModal) {
          const toggler = root.querySelector('modal-render-component.modal--active button[data-modal-ref]');
          if (toggler) toggler.dispatchEvent(new Event('click'));
        }
        if (element.dataset.redirect) return (window.location.href = element.dataset.redirect);
        if (element.dataset.modal === 'true') {
          const drawerToggler = document.querySelector(element.dataset.modalRef);
          if (drawerToggler) drawerToggler.dispatchEvent(new Event('click'));
        }
        if (element.recipientForm) element.recipientForm.resetRecipientForm();
      }
    });
};

/**
 * Main Initialization Function
 * @param {HTMLElement|Document} scope - The container to scope the initialization to.
 */
window.initProductPage = function(scope = document) {
  if (!scope || typeof scope.querySelector !== 'function') return;

  // Initialize XR elements if present
  if (scope.querySelector('[id^="ProductJSON-"]')) {
    window.ProductModel?.loadShopifyXR();
  }
  
  // Manual trigger for bundle sync
  scope.querySelectorAll('bundle-package').forEach(bundle => {
    const btn = bundle.querySelector('button');
    if (btn && btn.dataset.active === 'true') {
      bundle.syncSelection(btn);
    }
  });
};

// Initial run for the main page
window.addEventListener('DOMContentLoaded', () => window.initProductPage(document));
